import { Module, VuexModule, getModule, Mutation, Action } from 'vuex-module-decorators';
import store from '@/store';
import cms from '@/store/modules/cms';
import checkout from '@/store/modules/checkout';
import cart from '@/store/modules/cart';
import handleError from '@/commons/errorHandler';
import to from 'await-to-js';
import { i18n } from '@/i18n';
import {
    PaymentHandler,
    Totals,
    FinalizePaymentPayload,
    PaymentInformationRequest,
    PayResult,
} from '@/store/models';
import { initState } from '../stateinit';
import CashOnDeliveryHandler from './payments/cod';
import BraintreeHandler from './payments/braintree';
import YandexHandler from './payments/yandex';
import KlarnaHandler from './payments/klarna';
import { bus } from '@/events/bus';
import { getTotals } from '../api';
import HiPayHandler from './payments/hipay';
import { isFunction, isUndefined } from 'lodash';
import { redirectToLocation } from '@/commons/redirectManager';
import { success } from '@/commons/toast';

function methodCodeTranslator(methodCode: string) {
    const paymentAliases = store.state.options.payment.aliases;
    return paymentAliases.hasOwnProperty(methodCode) ? paymentAliases[methodCode] : methodCode;
}
const hipayHandler = new HiPayHandler();
const handlers: { [id: string]: PaymentHandler; } = {
    cod: new CashOnDeliveryHandler(),
    braintree: new BraintreeHandler(),
    yandex: new YandexHandler(),
    klarna: new KlarnaHandler(),
    hipay: hipayHandler,
    hipay_paypal: hipayHandler,
};

@Module({
    namespaced: true,
    name: 'payment',
    store,
    dynamic: true,
})
class PaymentModule extends VuexModule {
    public registry: string[] = [];
    public methodData: any[] = [];
    public salePayload: PayResult = {needsFinalization: false};
    public paymentSuccessInfo: any = initState('paymentSuccessInfo');
    public pendingInit: Array < {prom: Promise < boolean >, methodCode: string} > = [];
    public additionalData: any = {
        hipay_tokenization: {
            tokenize_card: false,
            tokenId: 0,
        },
    };

    get dataByMethod(): any {
        return (methodCode: string) => {
            methodCode = methodCodeTranslator(methodCode);
            const method = this.methodData.find((m: any) => m.methodCode === methodCode);
            return method ? method.data : {};
        };
    }

    @Mutation
    public register(methodCode: string) {
        this.registry.push(methodCode);
    }

    @Mutation
    public unregister(methodCode: string) {
        const mc = methodCodeTranslator(methodCode);
        const idx = this.registry.findIndex((m) => m === mc);
        if (idx !== -1) {
            this.registry.splice(idx, 1);
        }

        const initidx = this.pendingInit.findIndex((m) => m.methodCode === mc);
        if (initidx !== -1) {
            this.pendingInit.splice(initidx, 1);
        }
    }

    @Mutation
    public addMethodData(payload: {methodCode: string, data: any}) {
        this.methodData.push({methodCode: payload.methodCode, data: payload.data});
    }

    @Mutation
    public unsetMethodData(methodCode: string) {
        this.methodData.forEach((m: any, idx) => {
            if (m.methodCode === methodCode) {
                this.methodData.splice(idx, 1);
            }
        });
    }

    @Mutation
    public sale(payload: PayResult) {
        this.salePayload = payload;
    }

    @Action({rawError: true})
    public async registerMethod(payload: {methodCode: string, params?: any}): Promise<boolean> {
        let err: any;
        let registrationRes: any;
        const methodCode = methodCodeTranslator(payload.methodCode);
        if (!this.registry.includes(methodCode)) {
            this.register(methodCode);

            let resolveProm: any;
            const promise: any = new Promise((resolve) => {
                resolveProm = resolve;
            });
            this.pendingInit.push({prom: promise, methodCode});

            const params = payload.params || {};
            params.saleCommit = this.sale;
            [ err, registrationRes ] = await to(handlers[methodCode].init(params));
            if (err) {
                handleError(err);
                resolveProm(false);
            } else {
                const handler: any = handlers[methodCode];
                if (handler.getRegistrationPromiseResolveCallback) {
                    handler.getRegistrationPromiseResolveCallback()(registrationRes);
                }
                bus.$emit(`payment.${methodCode}.registered`, registrationRes);
                this.addMethodData({methodCode, data: registrationRes});
                resolveProm(true);
            }

            return promise;
        } else {
            return new Promise((resolve, reject) => {
                resolve(true);
            });
        }
    }

    @Action({rawError: true})
    public async unregisterMethod(method: string): Promise<boolean> {
        let err: any;
        let unregisterRes: any;
        const methodCode = methodCodeTranslator(method);
        if (this.registry.includes(methodCode)) {
            this.unregister(methodCode);
            [ err, unregisterRes ] = await to(handlers[methodCode].teardown(this.dataByMethod(methodCode)));
            return new Promise((resolve, reject) => {
                if (err) {
                    console.log(err);
                    handleError(err);
                    reject(false);
                } else {
                    this.unsetMethodData(methodCode);
                    resolve(true);
                }
            });
        } else {
            return new Promise((resolve, reject) => {
                resolve(true);
            });
        }
    }

    @Action({rawError: true})
    public async callMethodApi(payload: {paymentMethod: string, apiMethod: string, apiParams?: any}): Promise<boolean> {
        const methodCode = methodCodeTranslator(payload.paymentMethod);
        const prom: any = this.pendingInit.find((m) => m.methodCode === methodCode);
        if (prom && prom.prom) {
            await prom.prom;
            const methodData = this.dataByMethod(methodCode);
            methodData[payload.apiMethod](payload.apiParams);
        }
        return new Promise((resolve, reject) => {
            resolve(true);
        });
    }

    @Action({rawError: true})
    public async finalize(payload: FinalizePaymentPayload): Promise<boolean> {
        let request: PaymentInformationRequest;
        const checkoutMethod = isUndefined(payload.checkoutMethod) ? 'placeOrder' : payload.checkoutMethod;
        const doneCallback = isUndefined(payload.doneCallback) ? undefined : payload.doneCallback;
        const placeOrder = isUndefined(payload.doNotPlaceOrder) ? true : payload.doNotPlaceOrder;
        const storeRequest = isUndefined(payload.useStoredData) ? false : payload.useStoredData;
        const redirectUrl = payload.redirectUrl || '/checkout/onepage/success';
        const payData = payload.payData;

        if (!payload.paymentMethod) {
            payload.paymentMethod = checkout.selectedPayment;
        }

        if (storeRequest) {
            request = JSON.parse(localStorage.getItem('payment-request') as string);
        } else {
            const paymentExtensionAttributes: any = {
                agreement_ids: store.state.options.required_agreements,
                approved_agreements: cms.approvedAgreementIds,
            };

            if (payData.extension_attributes) {
                Object.assign(paymentExtensionAttributes, payData.extension_attributes);
            }

            const billingAddress = checkout.paymentBillingAddress;
            if (!billingAddress.telephone && !cart.cart.is_virtual && checkout.shippingAddress.telephone) {
                billingAddress.telephone = checkout.shippingAddress.telephone;
            }

            request = {
                cartId: cart.cartToken.toString() !== '' ? cart.cartToken : cart.cart.id,
                billingAddress,
                email: checkout.email,
                paymentMethod: {
                    method: payload.paymentMethod,
                    po_number: payData.po_number || undefined,
                    additional_data: payData.additional_data || undefined,
                    extension_attributes: paymentExtensionAttributes,
                },
            };
            delete request.billingAddress.default_shipping;
            delete request.billingAddress.default_billing;
            if (request.billingAddress.hasOwnProperty('id')) {
                delete request.billingAddress.id;
            }
        }
        return new Promise((resolve, reject) => {
            if (storeRequest) {
                localStorage.setItem('payment-request', JSON.stringify(request));
            }

            if (placeOrder) {
                const placeFn = () => {
                    return isFunction(checkoutMethod)
                        ? checkoutMethod(request)
                        : store.dispatch(`checkout/${checkoutMethod}`, request);
                };
                placeFn().then((orderId: string) => {
                    if (storeRequest) {
                        localStorage.removeItem('payment-request');
                    }
                    if (orderId) {
                        if (doneCallback) {
                            doneCallback(parseInt(orderId, 10));
                        } else {
                            success(i18n.t('Order placed').toString());
                            cart.reset();
                            checkout.reset();
                            setTimeout(() => {
                                redirectToLocation(redirectUrl);
                            }, 1000);
                        }
                    } else {
                        handleError(i18n.t('Error occurred while placing order').toString(), false);
                    }
                }).catch((e: any) => {
                    handleError(e);
                    if (e.response.data.message && e.response.data.message.indexOf('yandex_pending') === 0) {
                        redirectToLocation(e.response.data.message.replace('yandex_pending ', ''));
                    } else if (storeRequest) {
                        localStorage.removeItem('payment-request');
                    }
                });
            }

            resolve(true);
        });
    }

    @Action({rawError: true})
    public async validate(paymentMethod: string): Promise<boolean> {
        const methodCode = methodCodeTranslator(paymentMethod);
        const handler = handlers.hasOwnProperty(methodCode) ? handlers[methodCode] : undefined;

        return new Promise((resolve, reject) => {
            if (handler && handler.validate) {
                handler.validate().then((errors) => {
                    if (errors.length === 0) {
                        resolve(true);
                    } else {
                        reject(errors.join('\n'));
                    }
                });
            } else {
                resolve(true);
            }
        });
    }

    @Action({rawError: true})
    public async payReady(): Promise<Totals> {
        let err: any;
        let totals: any;
        [ err, totals ] = await to(getTotals());
        return new Promise((resolve, reject) => {
            Promise.all(this.pendingInit.map((p) => p.prom)).then((allres) => {
                resolve(totals);
            });
        });
    }

    @Action({rawError: true})
    public async pay(payload: {paymentMethod: string, totals: Totals}): Promise<PayResult> {
        const methodCode = methodCodeTranslator(payload.paymentMethod);
        const totals = payload.totals;
        let grandTotal = totals.grand_total;
        if (totals.base_currency_code !== totals.quote_currency_code) {
            grandTotal = totals.base_grand_total;
        }

        let err: string|null;
        let payRes: PayResult|undefined;
        if (handlers.hasOwnProperty(methodCode)) {
            [ err, payRes ] = await to(handlers[methodCode].pay(grandTotal));
        } else {
            err = i18n.t('Payment method not supported').toString();
        }

        return new Promise((resolve) => {
            if (!payRes) {
                payRes = {
                    error: err ? err : '',
                    needsFinalization: false,
                };
            }
            this.sale(payRes);
            resolve(payRes);
        });
    }
}

export default getModule(PaymentModule);

